import {
    TYPES_FIELD_NAME as ScreenOrientation_TYPES_FIELD_NAME,
    VALUES_FIELD_BYTEVALUE as ScreenOrientation_FIELD_BYTEVALUE,
} from '../../../model/impl/ScreenOrientationDto.private';
import type { DataMap, IDBVersionMigrator } from './interface';
import type { ILegacy_FloorPlanDto } from '../../../model';
import { ITransaction } from '../../../private/db/interface';

function _isLegacyFloorPlan(floorplan: object): floorplan is ILegacy_FloorPlanDto {
    return 'imageMediumId' in floorplan;
}

const module: IDBVersionMigrator = {
    version: 2,
    description: 'Workspace 2.0 support',
    mappings: {
        IBackgroundModeDto: '++id,name',
        IBeaconDto: '++id,name,uuid,major,minor,siteId',
        IBeaconSensitivityDto: '++id,name',
        ICountryDto: '++id,name',
        IDeviceAccountDto:
            '++id,userName,password,confirmPassword,siteId,modifiedById,isProductionUser',
        IFloorPlanDto:
            '++id,name,imageFileName,removeImage,imageMediumId,siteId,siteObjectsOnFloorPlanCount,styleId',
        IFloorPlanTourDto: '++id,floorPlanId,tourId',
        IFloorPlanTranslationDto:
            '++id,name,imageFileName,removeImage,imageMediumId,siteId,languageId,floorPlanId,positionX,positionY,zoom,siteObjectsOnFloorPlanCount,styleId',
        IFontFamilyDto: '++id,name',
        IFontStyleDto: '++id,name',
        IFontSizeUnitDto: '++id,name',
        IFontAlignmentDto: '++id,name',
        ILanguageDto: '++id,name,localizedName,isDefault',
        IMediumDto: '++id,mediumHash,fileName,mimeType,mediumSize,deleted,siteId',
        IMenuItemDto:
            '++id,name,namePropagation,imageFileName,removeImage,imageMediumId,siteId,toursCount,publishedToursCount,deleted,menuPosition,keypadNumber,contentStyleId,menuStyleId,isBackButtonOverride,backButtonStyleId,siteObjectId,int1,int2,int3,int4,bool1,bool2,tourId,menuTypeId,menuSubTypeId',
        IMenuItemKeywordDto: '++id,name,siteId,isTag',
        IMenuItemTranslationDto:
            '++id,menuItemId,languageId,name,deleted,subMenuName,description,backgroundImageFileName,removeBackgroundImage,backgroundImageMediumId,menuImageFileName,removeMenuImage,menuImageMediumId',
        IPinTypeDto: '++value,content,icon,text',
        IRoleDto: '++id,name',
        IScreenOrientationDto: '++id,name,byteValue',
        ISiteDto:
            '++id,name,logoFileName,removeLogo,logoMediumId,alarmEnabled,alarmPin,adminPin,languageSelectionMenu,tourSelectionMenu,clientId,screenOrientationId,majorVersion,minorVersion,toursCount,deviceAccountsCount,languageSelectionStyleId,tourSelectionStyleId,groupingSelectionMenu,screenOrientations,contactName,title,phone,mobile,addressLine1,addressLine2,county,postalCode,countryId,notes,bluetoothEnabled,streamMedia,streamingMediaPrefixUrl,showBatteryDot,disableSpeakerOutput,grayOutContentOnPlayback,backButtonStyleId,batteryThresholdRed,batteryThresholdOrange,screensaverEnabled,screensaverShowDelay,resetInactivityDelay,alwaysShowLanguageSelection,alwaysShowTourSelection,alwaysShowGroupingSelection,showGroupingSelectionHeader,showLanguageSelectionHeader,showTourSelectionHeader,showLanguageConfirmation,resetGroupingDaily,groupingMenuEnabled,groupingShow,groupingSelectionStyleId,defaultLanguageId,groupingHeader,languageHeader,tourHeader,siteObjectOnGroupingSelectionId,siteObjectOnGroupingSelectionInitName,siteObjectOnLanguageSelectionId,siteObjectOnLanguageSelectionInitName,siteObjectOnTourSelectionId,siteObjectOnTourSelectionInitName',
        ISiteObjectDto:
            '++id,name,int1,int2,int3,int4,altBoolean1,altBoolean2,isUniversal,keypadNumber,siteId,siteObjectTypeId,beaconId,beaconSensitivityId,fontSize,fontFamily,fontColor,menuPosition,nextObjectId,relatedObject1Id,relatedObject2Id,relatedObject3Id,relatedObject4Id,relatedObject5Id,removeForegroundImage,foregroundImageMediumId,foregroundImageFileName,removeBackgroundImage,backgroundImageMediumId,backgroundImageFileName,removeMenuImage,menuImageMediumId,menuImageFileName,namePropagation,longNamePropagation,descriptionPropagation,alternativeText1Propagation,alternativeText2Propagation,mediumPropagation,mediumFileName,removeMedium,backgroundColor,foregroundColor,styleId,toursCount,publishedToursCount,deleted,screenOrientations,keywords,isSiteObjectV2,siteObjectTypeV2Id',
        ISiteObjectOnFloorPlanDto:
            '++id,siteObjectId,floorPlanId,x,y,pinTypeId,pinColor,siteObjectName',
        ISiteObjectKeywordDto: '++id,name,siteId,isTag',
        ISiteObjectTranslationDto:
            '++id,siteObjectId,languageId,name,longName,description,alternativeText1,alternativeText2,mediumId,text,deleted',
        ISiteObjectTypeDto: '++id,defaultExtensions,name',
        ISiteSupportedLanguageDto: '++id,siteId,languageId',
        IStyleDto:
            '++id,parentId,isDefault,siteId,name,description,foregroundImageMediumId,backgroundImageMediumId,backgroundColor,menuImageMediumId,backgroundModeId,menuColor,primaryFontFamilyId,primaryFontStyleId,primaryFontSize,primaryFontSizeUnitId,primaryFontColor,primaryFontAlignmentId,secondaryFontFamilyId,secondaryFontStyleId,secondaryFontSize,secondaryFontSizeUnitId,secondaryFontColor,secondaryFontAlignmentId,tertiaryFontFamilyId,tertiaryFontStyleId,tertiaryFontSize,tertiaryFontSizeUnitId,tertiaryFontColor,tertiaryFontAlignmentId,foregroundImageFileName,removeForegroundImage,menuImageFileName,removeMenuImage,backgroundImageFileName,removeBackgroundImage,hasChildren,primaryColor,secondaryColor,tertiaryColor,quaternaryColor',
        ITourDto:
            '++id,name,fontColor,fontSize,fontName,backgroundColor,foregroundColor,description,siteId,tourTypeId,tourSubTypeId,styleId,tourStatusId,stage1,stage2,stage3,stage4,createdById,modifiedById,version,minorVersion,publishedVersion,testVersion,internalName,grayOutContentOnPlayback,qrCode,visibilityScheduleShow,visibilityScheduleHide,siteObjectPlayOnLaunchId,showFloorplanOnMenuOnly,hideOnTourSelect,backButtonStyleId,hideKeypadNumber,screenOrientations',
        ITourMenuItemDto: '++id,menuItemId,tourId,menuOrder',
        ITourRelatedSiteObjectDto:
            '++id,tourId,elementOrder,siteObjectId,menuItemId,parentTourRelatedSiteObjectId',
        ITourStatusDto: '++id,name',
        ITourSubTypeDto: '++id,name,tourTypeId',
        ITourTranslationDto:
            '++name,completed,tourSupportedLanguageId,mediumId,mediumFileName,removeMedium',
        ITourTypeDto: '++id,name',
        ITourSupportedLanguageDto: '++id,siteSupportedLanguageId,tourId,languageId',
        IUserDto:
            '++id,userName,firstName,lastName,avatarFileName,removeAvatar,avatar,avatarMimeType,isCmsUser,emailConfirmed,siteId,managedClientId,managedSiteId,modifiedById,mainRole,workingForClientId,mainRoleHR',
        ILegacy_FloorPlanDto:
            '++id,name,imageFileName,removeImage,imageMediumId,siteId,siteObjectsOnFloorPlanCount,styleId',
    },
    upgrade: (transaction: ITransaction) => {
        return new Promise(async function (resolve, reject) {
            try {
                /* Move IFloorPlanDto to ILegacy_FloorPlanDto (no move support, must add+delete) */
                const allFloorplansLegacy = await transaction.table('IFloorPlanDto').toArray();
                await transaction.table('ILegacy_FloorPlanDto').bulkAdd(allFloorplansLegacy);
                const floorPlanLegacyPrimaryKeys = await transaction
                    .table('IFloorPlanDto')
                    .toCollection()
                    .keys();

                transaction
                    .table('IFloorPlanDto')
                    .bulkDelete(floorPlanLegacyPrimaryKeys)
                    .catch(error => {
                        throw error;
                    });

                /* ignore addition operations */
                /* ICountryDto - add table */
                /* IDeviceAccountDto - add table */
                /* IFloorPlanTourDto - add table */
                /* IFloorPlanTranslationDto - add table */

                /* IMenuItemDto -- add fields */
                /* IMenuItemKeywordDto - add table */
                /* IMenuItemTranslationDto - add table */
                /* IRoleDto - add table */
                /* IScreenOrientationDto - add field byteValue(manual migration) */
                transaction
                    .table('IScreenOrientationDto')
                    .toCollection()
                    .modify((obj: Record<string, unknown>) => {
                        const newObj = { ...obj };
                        const name = (obj['name'] as ScreenOrientation_TYPES_FIELD_NAME) ?? 'All';
                        newObj['byteValue'] = ScreenOrientation_FIELD_BYTEVALUE[name];
                        return newObj;
                    })
                    .catch(error => {
                        throw error;
                    });

                /* ISiteDto - add fields */
                /* ISiteObjectDto - add fields */
                /* IStyleDto - add fields */
                /* ITourRelatedSiteObjectDto - add fields */
                /* ITourDto - add fields
                /* ITourSupportedLanguageDto - add fields */
                /* ITourTranslationDto - add fields */
                /* IUserDto - add fields */
                /* ILegacy_FloorPlanDto - add fields */

                resolve();
            } catch (error) {
                reject(error);
            }
        });
    },
    preInsertCleanup: (dataIn: DataMap) => {
        const dataOut = { ...dataIn };

        const floorplans = dataOut['IFloorPlanDto'];

        if (floorplans.length > 0 && _isLegacyFloorPlan(floorplans[0])) {
            /* move to Legacy interface & remove existing */
            dataOut['ILegacy_FloorPlanDto'] = floorplans;
            dataOut['IFloorPlanDto'] = [];
        }

        return dataOut;
    },
};

export default module;
